import {Box} from '@chakra-ui/react';
import {useEffect} from 'react';
import {useRouter} from 'next/router';
import {GetStaticProps} from 'next';
import {ResponsiveImageType} from 'react-datocms';
import {ResultOf} from '@graphql-typed-document-node/core';
import {VALENTINES_DAY_MASCARA} from '../../shared/consts';
import {request} from '../../graphql/api';
import {useUserQuery} from '../../graphql/generated';
import {
  StaticGenerationAssetmodelDocument,
  OwnAPiecePageDocument,
  BenefitTierRecord,
} from '../../graphql/datocms-generated';
import {useCurrentAssetOverview} from '../../hooks/current-asset-overview';
import {PageLayout} from '../../components/global/page-layout';
import {ProductMobileNavBar} from '../../components/global/product-mobile-nav-bar';
import {NavBar} from '../../components/global/nav-bar';
import {getCmsTheme, formatDateShorthand} from '../../utils/utils';
import {OwnAPieceHero} from '../../components/own-a-piece/own-a-piece-hero';
import {ProductHeader} from '../../components/marketplace/widgets/product-header';
import {PiecesRemaining} from '../../components/marketplace/widgets/pieces-remaining';
import {ProductListing} from '../../components/marketplace/widgets/product-listing';
import {MarketplaceWidgets} from '../../components/own-a-piece/marketplace-widgets';
import {PurchasePanel} from '../../components/own-a-piece/purchase-panel';
import {ExperienceBanksy} from '../../components/banksy-page/experienceBanksy';
import {BanksyNftBanner} from '../../components/banksy-page/banksyNftBanner';

interface OwnAPiecePageProps {
  pageData: ResultOf<typeof OwnAPiecePageDocument>['assetModel'];
}

const OwnAPiecePage: React.FC<OwnAPiecePageProps> = ({pageData}) => {
  const router = useRouter();
  const asset = useCurrentAssetOverview(pageData?.assetId || '');
  const userQuery = useUserQuery();

  const themeColors = getCmsTheme(pageData?.theme || '');
  const productSlug = pageData?.product?.slug || pageData?.slug;
  const activeProductStatus = [
    'OFFERING_OPEN',
    'OFFERING_END',
    'OFFERING_PENDING',
    'OFFERING_CLOSED',
    'SOLD_OUT',
  ];

  useEffect(() => {
    if (
      !asset.loading &&
      (asset.status === 'INACTIVE' || asset.status === 'COMMING_SOON')
    ) {
      router.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.loading]);

  const handleButtonPush = () => {
    const top = document.getElementById('marketplace-listings');
    if (top) {
      top.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <PageLayout
      firstName={userQuery.data?.user?.firstName || ''}
      additionalContentStyles={{pt: 0, pb: 0}}
      hideShapes
      title={
        pageData?.ownAPiecePageTitle || 'Own a Piece of History | Showpiece'
      }
      CustomNavbar={
        <>
          <Box display={{base: 'block', lg: 'none'}}>
            <ProductMobileNavBar
              username={userQuery.data?.user?.firstName}
              background={themeColors?.primary}
              headerBaseText={pageData?.title || ''}
              accent={themeColors?.secondary || ''}
              variant="dark"
              productLink={productSlug || ''}
              purchaseLink={`own-a-piece/${productSlug}`}
              activeLink={`own-a-piece/${productSlug}`}
            />
          </Box>
          <Box display={{base: 'none', lg: 'flex'}}>
            <NavBar username={userQuery.data?.user?.firstName} />
          </Box>
        </>
      }
    >
      <OwnAPieceHero
        bgColor={themeColors?.primary || 'black'}
        image={pageData?.assetImage?.responsiveImage || undefined}
        setImageHeroBackground={pageData?.setImageHeroBackground}
      >
        <Box>
          <Box display="block">
            <ProductHeader
              href={`/${productSlug}` || ''}
              title={pageData?.title || ''}
              subTitle={
                pageData?.assetId !== 'valentines.day.mascara'
                  ? pageData?.subTitle
                  : undefined
              }
              offeringDisplayItems={
                activeProductStatus.includes(asset.status || '')
                  ? asset.offeringDisplayItems
                  : undefined
              }
              totalPieces={
                activeProductStatus.includes(asset.status || '')
                  ? asset.totalAssetPieces
                  : undefined
              }
            />
          </Box>
          {activeProductStatus.includes(asset.status || '') && (
            <>
              {asset.status !== 'OFFERING_CLOSED' && (
                <Box my={2}>
                  <PiecesRemaining
                    color={themeColors?.secondary || ''}
                    availablePiecesFromCurrentAssetOffering={
                      asset.availablePiecesFromCurrentAssetOffering
                    }
                    totalPiecesFromCurrentAssetOffering={
                      asset.totalPiecesFromCurrentAssetOffering
                    }
                    soldPiecesFromCurrentAssetOffering={
                      asset.soldPiecesFromCurrentAssetOffering
                    }
                  />
                </Box>
              )}
              <ProductListing
                totalAssetCustomersTransacted={
                  asset.totalAssetCustomersTransacted
                }
                price={asset.priceString}
                startDate={formatDateShorthand(asset.startDate)}
                endDate={formatDateShorthand(asset.endDate)}
                currencyPostFix={asset.pricing?.postFix || ''}
                currencySymbol={asset.pricing?.symbol || ''}
                assetId={pageData?.assetId}
              />
            </>
          )}
          {asset.status === 'MARKETPLACE' && (
            <MarketplaceWidgets
              availabilityWidgetButton={handleButtonPush}
              assetId={pageData?.assetId || ''}
              color={themeColors?.secondary || ''}
              totalPieces={asset.totalAssetPieces || 0}
              marketplaceAveragePrice={
                asset.marketplaceAveragePrice
                  ? // eslint-disable-next-line radix
                    parseInt(asset.marketplaceAveragePrice)
                  : 0
              }
            />
          )}
        </Box>
      </OwnAPieceHero>
      <PurchasePanel
        status={asset.status || ''}
        assetId={pageData?.assetId || ''}
        background={themeColors?.primary || ''}
        color={themeColors?.secondary || ''}
        benefits={pageData?.benefits as BenefitTierRecord[]}
        maxAssetsPerUser={asset.maxAssetsPerUser || 0}
        pricePerPiece={asset.pricePerPiece || 0}
        slug={pageData?.slug || ''}
        totalPieces={asset.totalAssetPiecesAvailable || 0}
        company={pageData?.company || ''}
        companyPrefix={pageData?.companyPrefix || ''}
        companyLogo={
          pageData?.companyLogo?.responsiveImage as ResponsiveImageType
        }
        marketplaceImage={
          pageData?.assetImage?.responsiveImage?.src || undefined
        }
        currencySymbol={asset.pricing?.symbol || ''}
        currencyPostFix={asset.pricing?.postFix || ''}
      />
      {pageData?.assetId === VALENTINES_DAY_MASCARA && (
        <Box mt="60px">
          <ExperienceBanksy />
          <BanksyNftBanner
            desktopImage="/assets/banksy/banksey-nft.png"
            mobileImage="/assets/banksy/banksey-nft-mobile.png"
          />
        </Box>
      )}
    </PageLayout>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export async function getStaticPaths() {
  const assetData = await request(StaticGenerationAssetmodelDocument);

  return {
    fallback: 'blocking',
    paths: assetData.allAssetModels.map(
      (asset) => `/own-a-piece/${asset.slug}`,
    ),
  };
}

export const getStaticProps: GetStaticProps = async (props) => {
  const pageData = await request(OwnAPiecePageDocument, {
    slug: props.params?.asset as string,
  });

  return {
    revalidate: 10,
    notFound: !pageData.assetModel,
    props: {
      pageData: pageData.assetModel,
    },
  };
};

export default OwnAPiecePage;
